import React, { useContext, useEffect, useState, useMemo } from 'react';
import { MoveToPriceLineContext, SelectionContext, FilteredInventoryContext, SelectedInventoryItemContext, FilteredSalesContext, SelectedSalesItemContext, AllSalesDataContext, AllInventoryDataContext } from './ControlBar';
import PricingCurvePage from '../PricingCurvePage';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import ErrorMessagePopup from '../../CommanService/ErrorMessagePopup';
import ConfirmationPopup from '../../CommanService/ConfirmationPopup';
import notificationService from '../../CommanService/notificationService';

const MainTab = () => {
  const moveToPriceLine = useContext(MoveToPriceLineContext);
  const { selectedRegion, selectedInterchange, selectedMlModel, selectedVariable, selectedGroup, mlModels, tbVariables, tbGroups, regions, allApisLoaded, showSoldParts, includeBrokered } = useContext(SelectionContext);
  const { filteredInventory } = useContext(FilteredInventoryContext);
  const { setSelectedInventoryItem } = useContext(SelectedInventoryItemContext);
  // Use local state for filteredSales
  const [filteredSales, setFilteredSales] = useState([]);
  const { setSelectedSalesItem } = useContext(SelectedSalesItemContext);
  const allSalesData = useContext(AllSalesDataContext);
  const allInventoryData = useContext(AllInventoryDataContext);
  const navigate = useNavigate();
  
  // State for train functionality
  const [isTraining, setIsTraining] = useState(false);
  const [isCheckingModel, setIsCheckingModel] = useState(false);
  const [isPredicting, setIsPredicting] = useState(false);
  
  // State for part type selection
  const [selectedPartType, setSelectedPartType] = useState('');

  // State for Add Model functionality
  const [isAddingModel, setIsAddingModel] = useState(false);

  // Filter variables based on selected group
  const filteredVariables = useMemo(() => {
    if (!selectedGroup?.value) {
      return [];
    }
    return tbVariables.filter(variable => variable.group_id === selectedGroup.value);
  }, [tbVariables, selectedGroup]);

  // Unified popup state - start with popup hidden until APIs are loaded
  const [popupState, setPopupState] = useState({
    isVisible: false, // Start with popup hidden
    message: '',
    subMessage: '',
    popupType: 'validation',
    customPosition: 'validation'
  });

  // Confirmation popup state for retraining
  const [confirmationPopupState, setConfirmationPopupState] = useState({
    isVisible: false,
    message: '',
    subMessage: ''
  });

  // Add state for pagination
  const [inventoryRowsPerPage, setInventoryRowsPerPage] = useState(10);
  const [salesRowsPerPage, setSalesRowsPerPage] = useState(10);

  // For inventory pagination
  const [inventoryPage, setInventoryPage] = useState(1);
  const paginatedInventory = filteredInventory.slice((inventoryPage - 1) * inventoryRowsPerPage, inventoryPage * inventoryRowsPerPage);
  const totalInventoryPages = Math.ceil(filteredInventory.length / inventoryRowsPerPage);

  // For sales pagination
  const [salesPage, setSalesPage] = useState(1);
  const paginatedSales = filteredSales.slice((salesPage - 1) * salesRowsPerPage, salesPage * salesRowsPerPage);
  const totalSalesPages = Math.ceil(filteredSales.length / salesRowsPerPage);

  useEffect(() => {
    setInventoryPage(1);
  }, [filteredInventory, inventoryRowsPerPage]);

  useEffect(() => {
    setSalesPage(1);
  }, [filteredSales, salesRowsPerPage]);

  // Start validation when all APIs are loaded
  useEffect(() => {
    if (allApisLoaded && !popupState.isVisible) {
      // Start validation after a short delay to ensure UI is ready
      setTimeout(() => {
        if (!selectedRegion) {
          setPopupState({
            isVisible: true,
            message: 'Please select a Region',
            subMessage: 'Select a region to continue',
            popupType: 'validation',
            customPosition: 'validation'
          });
        }
      }, 500);
    }
  }, [allApisLoaded, popupState.isVisible, selectedRegion]);

  // Validation checks and popup management - only run after APIs are loaded
  useEffect(() => {
    // Only run validation if all APIs are loaded
    if (!allApisLoaded) {
      return;
    }

    // Check for missing region
    if (!selectedRegion) {
      setPopupState({
        isVisible: true,
        message: 'Please select a Region',
        subMessage: 'Select a region to continue',
        popupType: 'validation',
        customPosition: 'validation'
      });
      return;
    }

    // Check for missing interchange
    if (selectedRegion && !selectedInterchange) {
      setPopupState({
        isVisible: true,
        message: 'Please select an Interchange Number',
        subMessage: 'Select an interchange number to continue',
        popupType: 'validation',
        customPosition: 'validation'
      });
      return;
    }

    // Check for missing ML Model
    if (selectedRegion && selectedInterchange && !selectedMlModel?.value) {
      setPopupState({
        isVisible: true,
        message: 'Please select an ML Model',
        subMessage: 'Select an ML model to continue',
        popupType: 'validation',
        customPosition: 'validation'
      });
      return;
    }

    // Check for missing Group
    if (selectedRegion && selectedInterchange && selectedMlModel?.value && !selectedGroup?.value) {
      setPopupState({
        isVisible: true,
        message: 'Please select a Group',
        subMessage: 'Select a group to continue',
        popupType: 'validation',
        customPosition: 'validation'
      });
      return;
    }

    // Check for missing Variable
    if (selectedRegion && selectedInterchange && selectedMlModel?.value && selectedGroup?.value && !selectedVariable?.value) {
      setPopupState({
        isVisible: true,
        message: 'Please select a Variable',
        subMessage: 'Select a variable to continue',
        popupType: 'validation',
        customPosition: 'validation'
      });
      return;
    }

    // Check for missing Part Type
    if (selectedRegion && selectedInterchange && selectedMlModel?.value && selectedGroup?.value && selectedVariable?.value && !selectedPartType) {
      setPopupState({
        isVisible: true,
        message: 'Please select a Part Type',
        subMessage: 'Select a part type to continue',
        popupType: 'validation',
        customPosition: 'validation'
      });
      return;
    }

    // If all validations pass, hide popup
    setPopupState(prev => ({ ...prev, isVisible: false }));
  }, [selectedRegion, selectedInterchange, selectedMlModel, selectedVariable, selectedGroup, selectedPartType, allApisLoaded]);

  // Handle ML Model selection
  const handleMlModelChange = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue) {
      const selectedModel = mlModels.find(model => model.ml_model_code === selectedValue);
      if (selectedModel) {
        // Update the context with the selected model
        // We need to access the setter from ControlBar
        window.dispatchEvent(new CustomEvent('mlModelSelected', { 
          detail: { 
            value: selectedModel.ml_model_code, 
            label: selectedModel.name 
          } 
        }));
      }
    }
    
    // Clear PredictedData array when ML Model changes
    if (window.updatePredictedData) {
      window.updatePredictedData([]);
    }
  };

  // Handle Variable selection
  const handleVariableChange = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue) {
      const selectedVar = tbVariables.find(variable => variable.variable_code === selectedValue);
      if (selectedVar) {
        // Update the context with the selected variable
        window.dispatchEvent(new CustomEvent('variableSelected', { 
          detail: { 
            value: selectedVar.variable_code, 
            label: selectedVar.variable_name 
          } 
        }));
      }
    }
    
    // Clear PredictedData array when Variable changes
    if (window.updatePredictedData) {
      window.updatePredictedData([]);
    }
  };

  // Handle Part Type selection
const handlePartTypeChange = (event) => {
  const selectedValue = event.target.value;
  setSelectedPartType(selectedValue);

  //  Add this to notify ControlBar
  window.dispatchEvent(new CustomEvent('parttypeSelected', {
    detail: selectedValue
  }));

  // Clear PredictedData array when Part Type changes
  if (window.updatePredictedData) {
    window.updatePredictedData([]);
  }
};


  // Handle Group selection
  const handleGroupChange = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue) {
      const selectedGrp = tbGroups.find(group => group.id === parseInt(selectedValue));
      if (selectedGrp) {
        // Update the context with the selected group
        window.dispatchEvent(new CustomEvent('groupSelected', { 
          detail: { 
            value: selectedGrp.id, 
            label: selectedGrp.group_name 
          } 
        }));
        
        // Clear the previously selected variable when group changes
        // This ensures that the user must select a new variable for the new group
        window.dispatchEvent(new CustomEvent('variableSelected', { 
          detail: null 
        }));
      }
    }
    
    // Clear PredictedData array when Group changes
    if (window.updatePredictedData) {
      window.updatePredictedData([]);
    }
  };

  const viewInventory = (item) => {
    // Send the full item data to the detail page
   
    setSelectedInventoryItem(item);
    navigate('/control-bar/part-detail');
  };
  const viewsold = (item) => {
    // Send the full item data to the detail page
    
    setSelectedSalesItem(item);
    navigate('/control-bar/sales-detail');
  };

  // Handle Train Model
  const handleTrainModel = async () => {
    // Validation is already handled by useEffect above
    if (!selectedMlModel?.value || !selectedGroup?.value || !selectedVariable?.value || !selectedPartType || isTraining || isCheckingModel) {
      return;
    }

    setIsCheckingModel(true);
    try {
      // Check if model already exists
      const modelExistsResponse = await apiService.CheckModelExists(selectedMlModel.value, selectedVariable.value, selectedPartType);
      
      if (modelExistsResponse.success && modelExistsResponse.data) {
        // Model exists, show confirmation popup
        setConfirmationPopupState({
          isVisible: true,
          message: 'Model already exists',
          subMessage: `A ${selectedMlModel.value} model for ${selectedVariable.value} is already trained. Do you want to retrain it?`
        });
        return;
      }
      
      // Model doesn't exist, proceed with training
      await performTraining();
      
    } catch (error) {
      console.error('Error checking model existence:', error);
      // If check fails, proceed with training anyway
      await performTraining();
    } finally {
      setIsCheckingModel(false);
    }
  };

  // Perform the actual training
  const performTraining = async () => {
    setIsTraining(true);
    try {
      const response = await apiService.TrainModel(selectedMlModel.value, selectedVariable.value, selectedPartType);
      
      // Show success message
      setPopupState({
        isVisible: true,
        message: response.message || 'Model trained successfully!',
        subMessage: 'The model is now ready for predictions.',
        popupType: 'success',
        customPosition: 'validation'
      });

    } catch (error) {
      // Show error message with actual API response message
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred during training';
      setPopupState({
        isVisible: true,
        message: 'Training failed',
        subMessage: errorMessage,
        popupType: 'error',
        customPosition: 'validation'
      });
      console.error('Training failed:', error);
    } finally {
      setIsTraining(false);
    }
  };

  // Handle confirmation popup actions
  const handleConfirmRetrain = async () => {
    setConfirmationPopupState({ isVisible: false, message: '', subMessage: '' });
    await performTraining();
  };

  const handleCancelRetrain = () => {
    setConfirmationPopupState({ isVisible: false, message: '', subMessage: '' });
    setIsCheckingModel(false);
  };

  // Handle Add Model
  const handleAddModel = async () => {
    // Check if a model is selected
    if (!selectedMlModel?.value) {
      
      notificationService.showError('Select an ML model before adding it');
      return;
    }

    // Get user data from session storage
    const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
    const createdBy = userData.id;
    const createdByName = userData.first_name;

    setIsAddingModel(true);
    
    try {
      const modelData = {
        model_name: selectedMlModel.label || selectedMlModel.value,
        created_by: createdBy,
        create_by_name: createdByName
      };

      const response = await apiService.AddModelType(modelData);
      
      // Show success toast message
      notificationService.showSuccess(response.message );

    } catch (error) {
      // Show error toast message
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while adding the model';
      notificationService.showError(errorMessage);
      console.error('Add model failed:', error);
    } finally {
      setIsAddingModel(false);
    }
  };

  // Handle Predict Model
  const handlePredictModel = async () => {
    // Validation is already handled by useEffect above
    if (!selectedMlModel?.value || !selectedVariable?.value || !selectedRegion || !selectedInterchange || !selectedGroup?.value || !selectedPartType) {
      return;
    }

    setIsPredicting(true);
    
    // Clear PredictedData array before making prediction
    if (window.updatePredictedData) {
      window.updatePredictedData([]);
    }
    
    try {
      // Create input string with region, interchange and part type
      const inputData = JSON.stringify({
        region: selectedRegion,
        interchange_number: selectedInterchange,
        part_type: selectedPartType
      });

      const response = await apiService.Predict(selectedMlModel.value, selectedVariable.value, inputData, selectedPartType);
      
      // Get the weightage of the selected variable
      const selectedVarData = tbVariables.find(variable => variable.variable_code === selectedVariable.value);
      const weightage = selectedVarData ? selectedVarData.weightage : 100; // Default to 100 if not found
      
      // Reset the predicted_price in response.data based on weightage
      if (response && response.success && response.data && Array.isArray(response.data)) {
        const adjustedData = response.data.map(item => ({
          ...item,
          predicted_price: (item.predicted_price / 100) * weightage,
          mileage: item.mileage * 1000
        }));
        
        // Update the global PredictedData array in PricingCurvePage with adjusted data
        if (window.updatePredictedData) {
          window.updatePredictedData(adjustedData);
        }
      }
    } catch (error) {
      // Show error message with actual API response message
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred during prediction';
      setPopupState({
        isVisible: true,
        message: 'Prediction failed',
        subMessage: errorMessage,
        popupType: 'error',
        customPosition: 'validation'
      });
      console.error('Prediction failed:', error);
    } finally {
      setIsPredicting(false);
    }
  };

  // Close popup handler
  const closePopup = () => {
    setPopupState(prev => ({ ...prev, isVisible: false }));
    
    // After closing any popup, re-trigger validation checks
    setTimeout(() => {
      // Check for missing region
      if (!selectedRegion) {
        setPopupState({
          isVisible: true,
          message: 'Please select a Region',
          subMessage: 'Select a region to continue',
          popupType: 'validation',
          customPosition: 'validation'
        });
        return;
      }

      // Check for missing interchange
      if (selectedRegion && !selectedInterchange) {
        setPopupState({
          isVisible: true,
          message: 'Please select an Interchange Number',
          subMessage: 'Select an interchange number to continue',
          popupType: 'validation',
          customPosition: 'validation'
        });
        return;
      }

      // Check for missing ML Model
      if (selectedRegion && selectedInterchange && !selectedMlModel?.value) {
        setPopupState({
          isVisible: true,
          message: 'Please select an ML Model',
          subMessage: 'Select an ML model to continue',
          popupType: 'validation',
          customPosition: 'validation'
        });
        return;
      }

      // Check for missing Group
      if (selectedRegion && selectedInterchange && selectedMlModel?.value && !selectedGroup?.value) {
        setPopupState({
          isVisible: true,
          message: 'Please select a Group',
          subMessage: 'Select a group to continue',
          popupType: 'validation',
          customPosition: 'validation'
        });
        return;
      }

      // Check for missing Variable
      if (selectedRegion && selectedInterchange && selectedMlModel?.value && selectedGroup?.value && !selectedVariable?.value) {
        setPopupState({
          isVisible: true,
          message: 'Please select a Variable',
          subMessage: 'Select a variable to continue',
          popupType: 'validation',
          customPosition: 'validation'
        });
        return;
      }

      // Check for missing Part Type
      if (selectedRegion && selectedInterchange && selectedMlModel?.value && selectedGroup?.value && selectedVariable?.value && !selectedPartType) {
        setPopupState({
          isVisible: true,
          message: 'Please select a Part Type',
          subMessage: 'Select a part type to continue',
          popupType: 'validation',
          customPosition: 'validation'
        });
        return;
      }
    }, 100); // Small delay to ensure the previous popup is fully closed
  };

  // Check if train button should be disabled
  const isTrainDisabled = !selectedMlModel?.value || !selectedGroup?.value || !selectedVariable?.value || !selectedPartType || isTraining || isCheckingModel;
  
  // Check if predict button should be disabled
  const isPredictDisabled = isPredicting || !selectedMlModel?.value || !selectedVariable?.value || !selectedRegion || !selectedInterchange || !selectedGroup?.value || !selectedPartType;

  // Update filteredSales only in useEffect
  // Now filters sales data by: Region + Interchange + Part Name (part_name field)
  useEffect(() => {
    if (selectedRegion && selectedInterchange && selectedPartType && allSalesData.length > 0) {
      let filtered = allSalesData.filter(sale => 
        sale.region === selectedRegion && 
        sale.interchange_number === selectedInterchange &&
        sale.part_name === selectedPartType
        // Now filtering by region, interchange, AND part_name to match selected part type
      );

      console.log('Initial filtered sales:', {
        region: selectedRegion,
        interchange: selectedInterchange,
        partType: selectedPartType,
        partNameFilter: `Only showing sales with part_name = "${selectedPartType}"`,
        count: filtered.length,
        sample: filtered.slice(0, 2)
      });

      // Apply price adjustments and processing similar to R app
      filtered = filtered.map(sale => {
        // Calculate price ratio for adjustments
        const priceRatio = sale.instock_screen_price && sale.instock_screen_price > 0 
          ? sale.all_parts_gross_sales / sale.instock_screen_price 
          : null;

        // Define customer categories that get price adjustments (same as R app)
        const priceAdjustmentCategories = [
          "HOTLINE MEMBER", "PINNET MEMBER", "RECYCLER", "SALVAGE POOL",
          "SPECIAL 1", "SPECIAL 2", "SPECIAL 3", "SPECIAL 4", "SPECIAL 5", "SPECIAL 6",
          "SUPPLIER", "YARD"
        ];

        // Apply price adjustments based on customer category
        let adjustedPrice = sale.all_parts_gross_sales;
        if (priceRatio !== null && priceRatio <= 0.91 && 
            priceAdjustmentCategories.includes(sale.customer_category)) {
          adjustedPrice = sale.all_parts_gross_sales * 1.10;
        }

        // Calculate days since sale for transparency
        const daysSinceSale = Math.floor((new Date() - new Date(sale.invoice_date)) / (1000 * 60 * 60 * 24));
        
        return {
          ...sale,
          // Use raw mileage value (not multiplied by 1000)
          mileage_plot: sale.mileage,
          adjusted_price: adjustedPrice,
          // Determine plot color and symbol based on part_source
          plot_color: sale.part_source === 'Brokered' ? 'black' : 'purple',
          plot_symbol: 'x',
          days_since_sale: daysSinceSale,
          // Hover text for tooltips
          hover_text: `Invoice #: ${sale.invoice_number}<br>Detail ID: ${sale.detail_id}<br>Mileage: ${sale.mileage}<br>Price: $${sale.all_parts_gross_sales}<br>Adjusted Price: $${adjustedPrice.toFixed(2)}<br>Days In Stock: ${sale.days_instock}<br>Sale Date: ${sale.invoice_date ? sale.invoice_date.split('T')[0] : ''}`
        };
      });

      console.log('Processed sales data:', {
        totalProcessed: filtered.length,
        inStockCount: filtered.filter(s => s.part_source !== 'Brokered').length,
        brokeredCount: filtered.filter(s => s.part_source === 'Brokered').length,
        sampleProcessed: filtered.slice(0, 2)
      });

      // Apply checkbox filters
      // The showSoldParts and includeBrokered states are now managed by ControlBar
      // We need to get these values from the context
      // const { showSoldParts, includeBrokered } = useContext(SelectionContext); // This line is removed

      if (!showSoldParts) {
        filtered = [];
      } else if (!includeBrokered) {
        filtered = filtered.filter(sale => sale.part_source !== 'Brokered');
      }

      console.log('Final filtered sales after checkbox filters:', {
        showSoldParts,
        includeBrokered,
        finalCount: filtered.length
      });

      setFilteredSales(filtered);
    } else {
      if (!selectedRegion || !selectedInterchange || !selectedPartType) {
        console.log('X icons not plotted - missing conditions:', {
          hasRegion: !!selectedRegion,
          hasInterchange: !!selectedInterchange,
          hasPartType: !!selectedPartType,
          hasSalesData: allSalesData.length > 0
        });
      } else {
        // Check if there are any sales with matching part_name
        const salesWithMatchingPartName = allSalesData.filter(sale => 
          sale.region === selectedRegion && 
          sale.interchange_number === selectedInterchange &&
          sale.part_name === selectedPartType
        );
        
        if (salesWithMatchingPartName.length === 0) {
          console.log('X icons not plotted - no sales data with matching part_name:', {
            region: selectedRegion,
            interchange: selectedInterchange,
            selectedPartType: selectedPartType,
            availablePartNames: [...new Set(allSalesData
              .filter(sale => sale.region === selectedRegion && sale.interchange_number === selectedInterchange)
              .map(sale => sale.part_name)
              .filter(Boolean)
            )],
            totalSalesInRegion: allSalesData.filter(sale => 
              sale.region === selectedRegion && sale.interchange_number === selectedInterchange
            ).length
          });
        }
      }
      setFilteredSales([]);
    }
  }, [selectedRegion, selectedInterchange, selectedPartType, allSalesData, showSoldParts, includeBrokered]);

  return (
    <div>
      <div className="row control_row">
        <div className="col-md-12">
          <div className="control_blk_containers filter_blk main_control_filter">
            <div className="row control_row">
              <div className="col-md-3">
                <label className="font_14 fw-med text_black mb_5 d-block">ML Models</label>
                <div className="position-relative">
                  <select 
                    className="form-control w-100" 
                    value={selectedMlModel?.value || ''}
                    onChange={handleMlModelChange}
                  >
                    <option value="">Select</option>
                    {mlModels.map((model, index) => (
                      <option key={index} value={model.ml_model_code}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                  <span className="dropown_blk"></span>
                </div>
              </div>
              <div className="col-md-3">
                <label className="font_14 fw-med text_black mb_5 d-block">Groups</label>
                <div className="position-relative">
                  <select 
                    className="form-control w-100" 
                    value={selectedGroup?.value || ''}
                    onChange={handleGroupChange}
                  >
                    <option value="">Select</option>
                    {tbGroups.map((group, index) => (
                      <option key={index} value={group.id}>
                        {group.group_name}
                      </option>
                    ))}
                  </select>
                  <span className="dropown_blk"></span>
                </div>
              </div>
              <div className="col-md-3">
                <label className="font_14 fw-med text_black mb_5 d-block">Variables</label>
                <div className="position-relative">
                  <select 
                    className="form-control w-100" 
                    value={selectedVariable?.value || ''}
                    onChange={handleVariableChange}
                  >
                    <option value="">Select</option>
                    {filteredVariables.map((variable, index) => (
                      <option key={index} value={variable.variable_code}>
                        {variable.variable_name}
                      </option>
                    ))}
                  </select>
                  <span className="dropown_blk"></span>
                </div>
              </div>
              <div className="col-md-3">
                <label className="font_14 fw-med text_black mb_5 d-block">Part Type</label>
                <div className="position-relative">
                  <select 
                    className="form-control w-100" 
                    value={selectedPartType || ''}
                    onChange={handlePartTypeChange}
                  >
                                         <option value="">Select</option>
                     <option value="ENGINE">Engine</option>
                     <option value="TRANSMISSION">Transmission</option>
                  </select>
                  <span className="dropown_blk"></span>
                </div>
              </div>
              <div className="col-md-3 mt_21">
                <button 
                  className="primary_btn mr_15" 
                  onClick={handleTrainModel}
                  disabled={isTrainDisabled}
                  style={{
                    backgroundColor: isTrainDisabled ? '#cccccc' : '#ff6600',
                    color: isTrainDisabled ? '#666666' : '#ffffff',
                    cursor: isTrainDisabled ? 'not-allowed' : 'pointer',
                    opacity: isTrainDisabled ? 0.6 : 1
                  }}
                >
                  {isCheckingModel ? 'Checking...' : isTraining ? 'Training...' : 'Train'}
                </button>
                <button 
                  className="primary_btn secondary_btn"
                  onClick={handlePredictModel}
                  disabled={isPredictDisabled}
                  style={isPredictDisabled ? {
                    backgroundColor: '#cccccc',
                    color: '#666666',
                    cursor: 'not-allowed',
                    opacity: 0.6
                  } : {}}
                >
                  {isPredicting ? 'Predicting...' : 'Predict'}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-12">
          <div className="box_header">
            <h3 className="box_title">Pricing Curve</h3>
            <div className="box_body">
              <PricingCurvePage 
                moveToPriceLine={moveToPriceLine} 
                processedSalesData={filteredSales}
                showSoldParts={showSoldParts}
                includeBrokered={includeBrokered}
              />
            </div>
          </div>
        </div>
        
        <div className="col-md-12 mt_15" style={{ textAlign: 'left' }}>
          <button 
            className="primary_btn"
            onClick={handleAddModel}
            disabled={isAddingModel}
            style={{
              backgroundColor: isAddingModel ? '#cccccc' : '#ff6600',
              color: isAddingModel ? '#666666' : '#ffffff',
              padding: '8px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: isAddingModel ? 'not-allowed' : 'pointer',
              opacity: isAddingModel ? 0.6 : 1
            }}
          >
            {isAddingModel ? 'Adding Model...' : 'Add Model'}
          </button>
        </div>
        {/* Use full code for the new page called Price Analysis We will use this in the future*/}

        {/* <div className="col-md-6">
          <div className="control_blk_containers">
            <h3 className="font_16 fw-reg text_black mb_10">Pricing Data</h3>
            <table className="w-100 table table_color">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Interchange Number</th>
                </tr>
              </thead>
              <tbody>
                {selectedRegion && selectedInterchange ? (
                  <tr>
                    <td>{selectedRegion}</td>
                    <td>{selectedInterchange}</td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={2}>No data selected</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div> */}
        {/* <div className="col-md-6">
          <div className="control_blk_containers">
            <h3 className="font_16 fw-reg text_black mb_10">Interchange Applications</h3>
            <table className="w-100 table table_color">
              <thead>
                <tr>
                  <th>Model Name</th>
                  <th>Model Year</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {filteredInventory && filteredInventory.length > 0 
                      ? [...new Set(filteredInventory.map(item => item.model_name).filter(Boolean))].join(', ')
                      : 'No data available'
                    }
                  </td>
                  <td>
                    {filteredInventory && filteredInventory.length > 0 
                      ? [...new Set(filteredInventory.map(item => item.model_year).filter(Boolean))].join(', ')
                      : 'No data available'
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div> */}
        {/* <div className="col-md-12">
          <div className="control_blk_containers">
            <h3 className="font_16 fw-reg text_black mb_10">Filtered Inventory</h3>
            <div className="d-flex justify-content-between align-items-center mb_15">
              <div className="d-flex align-items-center">
                <span className="font_14 text_black mr_10">Show</span>
                <select 
                  className="form-control" 
                  style={{ width: '80px', marginRight: '10px' }}
                  value={inventoryRowsPerPage}
                  onChange={(e) => setInventoryRowsPerPage(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
                <span className="font_14 text_black">entries</span>
              </div>
            </div>
            <table className="w-100 table table_color filtered-inventory-table">
              <thead>
                <tr>
                  <th>Stock_number</th>
                  <th>Unique</th>
                  <th>Model Name</th>
                  <th>Model Year</th>
                  <th>Mileage</th>
                  <th>Part Price</th>
                  <th>Days Instock</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInventory && paginatedInventory.length > 0 ? (
                  paginatedInventory.map((item, idx) => (
                    <tr key={item.unique || idx}>
                      <td>{item.stock_number}</td>
                      <td>{item.unique}</td>
                      <td>{item.model_name}</td>
                      <td>{item.model_year}</td>
                      <td>{item.mileage}</td>
                      <td>{item.part_price}</td>
                      <td>{item.days_instock}</td>
                      <td><button className="clear_btn pri_btn" onClick={() => viewInventory(item)}>View</button></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8}>No data found</td>
                  </tr>
                )}
              </tbody>
            </table>
            {totalInventoryPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt_10">
                <div>
                  <span className="font_14 text_black">
                    Page {inventoryPage} of {totalInventoryPages}
                  </span>
                </div>
                <div>
                  <button 
                    className="clear_btn filter_btn mr_10"
                    onClick={() => setInventoryPage(p => Math.max(1, p - 1))}
                    disabled={inventoryPage === 1}
                    style={{ minWidth: 100 }}
                  >
                    Previous
                  </button>
                  <button 
                    className="clear_btn filter_btn"
                    onClick={() => setInventoryPage(p => Math.min(totalInventoryPages, p + 1))}
                    disabled={inventoryPage === totalInventoryPages}
                    style={{ minWidth: 100 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div> */}
        {/* <div className="col-md-12">
          <div className="control_blk_containers">
            <h3 className="font_16 fw-reg text_black mb_10">Sold Parts (Matching)</h3>
            <div className="d-flex justify-content-between align-items-center mb_15">
              <div className="d-flex align-items-center">
                <span className="font_14 text_black mr_10">Show</span>
                <select 
                  className="form-control" 
                  style={{ width: '80px', marginRight: '10px' }}
                  value={salesRowsPerPage}
                  onChange={(e) => setSalesRowsPerPage(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
                <span className="font_14 text_black">entries</span>
              </div>
            </div>
            <table className="w-100 table table_color filtered-inventory-table">
              <thead>
                <tr>
                  <th>invoice_date</th>
                  <th>invoice_number</th>
                  <th>detail_id</th>
                  <th>part_source</th>
                  <th>mileage</th>
                  <th>all_parts_gross_sales</th>
                  <th>days_instock</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSales && paginatedSales.length > 0 ? (
                  paginatedSales.map((item, idx) => (
                    <tr key={item.detail_id || idx}>
                      <td>{item.invoice_date ? item.invoice_date.split('T')[0] : ''}</td>
                      <td>{item.invoice_number}</td>
                      <td>{item.detail_id}</td>
                      <td>{item.part_source}</td>
                      <td>{item.mileage}</td>
                      <td>{item.all_parts_gross_sales}</td>
                      <td>{item.days_instock}</td>
                      <td><button className="clear_btn pri_btn" onClick={() => viewsold(item)}>View</button></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8}>No data found</td>
                  </tr>
                )}
              </tbody>
            </table>
            {totalSalesPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt_10" style={{ width: '100%' }}>
                <div>
                  <span className="font_14 text_black">
                    Page {salesPage} of {totalSalesPages}
                  </span>
                </div>
                <div>
                  <button 
                    className="clear_btn filter_btn mr_10"
                    onClick={() => setSalesPage(p => Math.max(1, p - 1))}
                    disabled={salesPage === 1}
                    style={{ minWidth: 100 }}
                  >
                    Previous
                  </button>
                  <button 
                    className="clear_btn filter_btn"
                    onClick={() => setSalesPage(p => Math.min(totalSalesPages, p + 1))}
                    disabled={salesPage === totalSalesPages || totalSalesPages === 0}
                    style={{ minWidth: 100 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div> */}
      </div>

      {/* Unified Popup */}
      <ErrorMessagePopup
        message={popupState.message}
        subMessage={popupState.subMessage}
        isVisible={popupState.isVisible}
        onClose={closePopup}
        customPosition={popupState.customPosition}
        popupType={popupState.popupType}
      />

      {/* Confirmation Popup for Retraining */}
      <ConfirmationPopup
        message={confirmationPopupState.message}
        subMessage={confirmationPopupState.subMessage}
        isVisible={confirmationPopupState.isVisible}
        onConfirm={handleConfirmRetrain}
        onCancel={handleCancelRetrain}
        customPosition="validation"
      />
    </div>
  );
};

export default MainTab; 